import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { Calendar, User, ArrowRight } from "lucide-react";
import { format } from "date-fns";

const CATEGORIES = ["All", "Buying", "Selling", "Market Updates", "Community", "Investing", "Careers"] as const;
const POSTS_PER_PAGE = 12;

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image: string | null;
  author_name: string;
  category: string | null;
  published_at: string | null;
}

const Blog = () => {
  useEffect(() => {
    document.title = "South Jersey Real Estate Blog | Prime Realty Partners";
  }, []);

  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string>("All");
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPosts = async (category: string, offset = 0, append = false) => {
    if (offset === 0) setLoading(true);
    else setLoadingMore(true);

    let query = supabase
      .from("blog_posts")
      .select("id, title, slug, excerpt, featured_image, author_name, category, published_at")
      .in("status", ["published", "scheduled"])
      .lte("published_at", new Date().toISOString())
      .order("published_at", { ascending: false })
      .range(offset, offset + POSTS_PER_PAGE - 1);

    if (category !== "All") {
      query = query.eq("category", category);
    }

    const { data, error } = await query;
    if (error) console.error("Error fetching posts:", error);

    const newPosts = (data || []) as BlogPost[];
    setPosts(append ? (prev) => [...prev, ...newPosts] : newPosts);
    setHasMore(newPosts.length === POSTS_PER_PAGE);
    setLoading(false);
    setLoadingMore(false);
  };

  useEffect(() => {
    fetchPosts(activeCategory);
  }, [activeCategory]);

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
  };

  const handleLoadMore = () => {
    fetchPosts(activeCategory, posts.length, true);
  };

  return (
    <Layout>
      <SEOHead
        title="South Jersey Real Estate Blog | Prime Realty Partners"
        description="South Jersey real estate blog — market updates, home buying tips, community guides, and expert advice from Prime Realty Partners."
        canonicalUrl="https://www.prpsj.com/blog"
        keywords="south jersey real estate blog, nj home buying tips, selling advice nj, market updates south jersey"
        breadcrumbs={[{ name: "Home", url: "https://www.prpsj.com" }, { name: "Blog", url: "https://www.prpsj.com/blog" }]}
        schema={[{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Prime Realty Partners Blog",
          description: "Real estate tips, South Jersey market updates, home buying guides, and selling advice.",
          url: "https://www.prpsj.com/blog",
          publisher: {
            "@type": "Organization",
            name: "Prime Realty Partners",
          },
        }]}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/15 via-primary/5 to-background py-16 md:py-20">
        <div className="container">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Real Estate Blog
            </h1>
            <p className="text-xl text-muted-foreground">
              Tips, guides, and market insights for South Jersey home buyers and sellers.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="border-b border-border sticky top-16 md:top-20 z-30 bg-background/95 backdrop-blur">
        <div className="container py-3">
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {CATEGORIES.map((cat) => (
              <Button
                key={cat}
                variant={activeCategory === cat ? "default" : "outline"}
                size="sm"
                onClick={() => handleCategoryChange(cat)}
                className="whitespace-nowrap"
              >
                {cat}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-12 md:py-16">
        <div className="container">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i}>
                  <Skeleton className="h-48 w-full rounded-t-lg" />
                  <CardContent className="pt-4 space-y-3">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-lg text-muted-foreground">No blog posts yet. Check back soon!</p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.map((post) => (
                  <Link key={post.id} to={`/blog/${post.slug}`} className="group">
                    <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full flex flex-col">
                      {post.featured_image ? (
                        <img
                          src={post.featured_image}
                          alt={post.title}
                          width={400}
                          height={192}
                          className="h-48 w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="h-48 w-full bg-muted flex items-center justify-center">
                          <span className="text-4xl text-muted-foreground/30 font-heading font-bold">PRP</span>
                        </div>
                      )}
                      <CardContent className="pt-4 flex flex-col flex-1">
                        {post.category && (
                          <Badge variant="secondary" className="w-fit mb-2 text-xs">
                            {post.category}
                          </Badge>
                        )}
                        <h2 className="font-heading text-lg font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                          {post.title}
                        </h2>
                        {post.excerpt && (
                          <p className="text-sm text-muted-foreground line-clamp-3 mb-4 flex-1">
                            {post.excerpt}
                          </p>
                        )}
                        <div className="flex items-center justify-between text-xs text-muted-foreground mt-auto pt-3 border-t border-border">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {post.author_name}
                          </span>
                          {post.published_at && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(post.published_at), "MMM d, yyyy")}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>

              {hasMore && (
                <div className="text-center mt-10">
                  <Button variant="outline" size="lg" onClick={handleLoadMore} disabled={loadingMore}>
                    {loadingMore ? "Loading..." : "Load More Posts"}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Blog;
